<script lang="ts">
import { computed, defineComponent, nextTick, ref, unref } from 'vue';

import {
  CdsTransition,
  makeDisabledProps,
  makeFieldProps,
  makeFocusProps,
  makeIconEvents,
  makeIconProps,
  makeInputProps,
  makeItemsProps,
  makeLoadingProps,
  makeReadonlyProps,
  makeThemeProps,
  makeValidationProps,
  provideTheme,
  useField,
  useFilteredInputAttrs,
  useFilteredInputProps,
  useFocus,
  useIcon,
  useInput,
  useItems,
  useLoading,
  useProxyModel
} from '../../composable';
import { CdsIcon } from '../CdsIcon';
import { CdsInput, CdsInputDescription, CdsInputLabel } from '../CdsInput';
import { CdsLoader } from '../CdsLoader';
import { CdsFunctionalButton } from '../CdsButton';

export const componentName = 'CdsSelect';
export default defineComponent({
  name: componentName,
  components: { CdsInput, CdsInputLabel, CdsInputDescription, CdsIcon, CdsLoader, CdsFunctionalButton, CdsTransition },
  props: {
    ...makeFieldProps(),
    ...makeInputProps(),
    ...makeValidationProps(),
    ...makeIconProps(),
    ...makeIconEvents(),
    ...makeFocusProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeLoadingProps(),
    ...makeItemsProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие при фокусе
     * @type { boolean }
     */
    'update:focused': (value: boolean) => true,
    /**
     * Событие при изменении значения (v-model)
     * @type { string }
     */
    'update:modelValue': (value: string) => true,
    /**
     * Событие очистки поля ввода
     */
    'click:clear': (event: MouseEvent) => true,
    /**
     * Событие клика по иконке перед значением поля
     */
    'click:prepend': (event: MouseEvent) => true,
    /**
     * Событие клика по иконке после значения поля
     */
    'click:append': (event: MouseEvent) => true,
    /**
     * Событие клика по иконке внутри элемента перед значением поля
     */
    'click:prependInner': (event: MouseEvent) => true,
    /**
     * Событие клика по иконке внутри элемента после значения поля
     */
    'click:appendInner': (event: MouseEvent) => true
  },
  setup(props, { slots, attrs, emit, expose }) {
    const model = useProxyModel(props, 'modelValue');
    const { themeClasses } = provideTheme(props);
    const { id, hasLabel, hasDescription } = useInput(props, slots, componentName);
    const { hasPrepend, hasAppend, iconClasses } = useIcon(props, slots, componentName);
    const { hasLoading, loadingClasses } = useLoading(props);
    const { isFocused, focus, blur } = useFocus(props, componentName);
    const {
      hasPrependInnerIcon,
      hasAppendInnerIcon,
      fieldClasses,
      fieldProps,
      handleClickPrependInner,
      handleClickAppendInner
    } = useField(props, slots, componentName);
    const { items, itemsProps } = useItems(props);
    const { inputProps } = useFilteredInputProps(props, [
      ...fieldProps,
      ...itemsProps,
      'modelValue',
      'focused',
      'loading'
    ]);
    const { rootAttrs, inputAttrs } = useFilteredInputAttrs(attrs);
    const isActive = computed(() => !!unref(model) || props.active);
    const hasClear = computed(() => props.clearable);

    const fieldRef = ref<HTMLElement>();
    const selectRef = ref<HTMLSelectElement>();
    const validationRef = ref<typeof CdsInput>();

    const selectClasses = computed(() => ({
      'cds-select--active': unref(isActive),
      'cds-select--has-prefix': !!props.prefix,
      'cds-select--has-suffix': !!props.suffix,
      'cds-select--has-loading': props.loading,
      'cds-select--has-clear': props.clearable,
      'cds-select--has-description': unref(hasDescription)
    }));

    const optionProps = computed(() => ({
      selected: unref(model)
    }));

    const onInput = (event: Event) => {
      model.value = (event.target as HTMLSelectElement).value;
    };

    const onFocus = () => {
      if (unref(selectRef) !== document.activeElement) {
        unref(selectRef)?.focus();
      }

      if (!isFocused.value) focus();
    };

    const onClear = (event: MouseEvent) => {
      event.stopPropagation();

      onFocus();

      nextTick(() => {
        model.value = null;

        emit('click:clear', event);
      });
    };

    expose({
      id,
      fieldRef,
      validationRef,
      selectRef
    });

    return {
      id,
      model,
      items,
      disabled: props.disabled,
      optionProps,
      fieldRef,
      selectRef,
      validationRef,
      isFocused,
      rootAttrs,
      inputAttrs,
      inputProps,
      hasLabel,
      hasDescription,
      hasPrepend,
      hasAppend,
      hasPrependInnerIcon,
      hasAppendInnerIcon,
      hasLoading,
      hasClear,
      fieldClasses,
      selectClasses,
      iconClasses,
      loadingClasses,
      themeClasses,
      onInput,
      onBlur: blur,
      onFocus,
      onClear,
      handleClickPrependInner,
      handleClickAppendInner
    };
  }
});
</script>

<template>
  <div ref="fieldRef" class="cds-select-field" :class="themeClasses" v-bind="rootAttrs">
    <cds-input-label v-if="hasLabel" :for="id" :text="label" :disabled="disabled">
      <!--
       @slot Лейбл
       @type string | html
     -->
      <slot name="label" />
    </cds-input-label>
    <cds-input-description v-if="hasDescription" :text="description">
      <!--
        @slot Описание
        @type string | html
      -->
      <slot name="description" />
    </cds-input-description>
    <cds-input
      :id="id"
      ref="validationRef"
      v-model="model"
      :focused="isFocused"
      class="cds-select"
      :class="[selectClasses, fieldClasses, iconClasses, loadingClasses]"
      v-bind="{ ...inputProps }"
    >
      <template v-if="hasPrepend" #prepend>
        <!--
          @slot Элемент перед значением поля
          @type string | html
        -->
        <slot name="prepend" />
      </template>
      <template #default="{ isDisabled, isReadonly, isValid }">
        <div v-if="hasPrependInnerIcon" class="cds-select__prepend-inner" @click="handleClickPrependInner">
          <!--
            @slot Элемент внутри перед значением поля
            @type string | html
          -->
          <slot name="prepend-inner" />
          <cds-icon v-if="!!prependInnerIcon" :name="prependInnerIcon" />
        </div>
        <span v-if="prefix" class="cds-select__prefix" @click="onFocus">{{ prefix }}</span>
        <select
          :id="id"
          ref="selectRef"
          :value="model"
          :disabled="isDisabled.value"
          :aria-invalid="error || isValid.value === false"
          :autofocus="autofocus"
          v-bind="{ ...inputAttrs }"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
        >
          <option value="" disabled hidden selected>{{ placeholder }}</option>
          <template v-for="(item, idx) in items" :key="`${item.value}-${idx}`">
            <option
              v-if="!item.children"
              :value="item.value"
              :label="item.title"
              :disabled="item.props.disabled || isReadonly.value"
              :selected="item.value === model"
            />
            <optgroup v-else :label="item.title">
              <option
                v-for="(childItem, childIdx) in item.children"
                :key="`${childItem.value}-${childIdx}`"
                :value="childItem.value"
                :label="childItem.title"
                :disabled="childItem.props.disabled || isReadonly.value"
                :selected="childItem.value === model"
              />
            </optgroup>
          </template>
          <slot v-bind="{ ...optionProps, disabled: isDisabled.value || isReadonly.value }" />
        </select>
        <div v-if="hasClear" class="cds-select__clear">
          <cds-functional-button
            :disabled="isDisabled.value"
            :title="clearLabel"
            icon="remove"
            class="cds-select__clear--button"
            :class="{ active: !!model }"
            @click="onClear"
            @mousedown.prevent.stop
          />
        </div>
        <span v-if="suffix" class="cds-select__suffix" @click="onFocus">{{ suffix }}</span>
        <div v-if="hasAppendInnerIcon || hasLoading" class="cds-select__append-inner" @click="handleClickAppendInner">
          <cds-transition transition="fade-transition" role="alert" aria-busy="true">
            <cds-loader v-if="hasLoading" size="xs" class="cds-select__loader" />
          </cds-transition>
          <!--
            @slot Элемент внутри после значения поля
            @type string | html
          -->
          <slot name="append-inner" />
          <cds-icon v-if="!!appendInnerIcon" :name="appendInnerIcon" />
        </div>
        <div class="cds-select__arrow">
          <cds-icon name="chevron-down" />
        </div>
      </template>
      <template v-if="hasAppend" #append>
        <!--
          @slot Элемент после значения поля
          @type string | html
        -->
        <slot name="append" />
      </template>
      <template #messages>
        <!--
          @slot Сообщения и подсказки
          @type string | html
        -->
        <slot name="messages" />
      </template>
    </cds-input>
  </div>
</template>

<style lang="scss">
@import './CdsSelect';
</style>
